/**
 * Schedule Upload Component - AI-powered schedule image analysis with manual review
 *
 * Multi-step workflow:
 * 1. Upload: User uploads schedule image (camera/gallery)
 * 2. Analyzing: AI analyzes image via Bedrock
 * 3. Review: User reviews and edits extracted appointments
 * 4. Complete: Appointments created successfully
 */

import { useMemo, useState } from 'react';
import { format, parse } from 'date-fns';
import {
  CalendarIcon,
  Check,
  CheckCircle2,
  ChevronsUpDown,
  Clock,
  ImagePlus,
  Loader2,
  Trash2,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useMembers } from '@/features/members/api/members-api';

import {
  type Appointment,
  useAnalyzeScheduleImage,
  useCreateAppointments,
} from '../api/scheduleUploadApi';
import type { UploadStep } from '../types';
import { normalizeTime } from '../utils/timeOptions';

const STEPS: { key: UploadStep; label: string }[] = [
  { key: 'upload', label: 'Upload' },
  { key: 'analyzing', label: 'Reading' },
  { key: 'review', label: 'Review' },
  { key: 'complete', label: 'Done' },
];

/** Generate time options in 15-minute increments for the select dropdown. */
function generateTimeOptions(): string[] {
  const options: string[] = [];
  for (let h = 6; h <= 20; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hour12 = h % 12 || 12;
      const ampm = h < 12 ? 'AM' : 'PM';
      const minutes = m.toString().padStart(2, '0');
      options.push(`${hour12}:${minutes} ${ampm}`);
    }
  }
  return options;
}

const TIME_OPTIONS = generateTimeOptions();

/** Parse a MM/DD/YYYY date string to a Date object. */
function parseDateString(dateStr: string): Date | undefined {
  if (!dateStr) return undefined;
  try {
    const d = parse(dateStr, 'MM/dd/yyyy', new Date());
    return isNaN(d.getTime()) ? undefined : d;
  } catch {
    return undefined;
  }
}

interface AppointmentWithClinician extends Appointment {
  clinician_id: string | null;
}

export function ScheduleUpload() {
  const [currentStep, setCurrentStep] = useState<UploadStep>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [appointments, setAppointments] = useState<AppointmentWithClinician[]>([]);
  const [openClinicianIdx, setOpenClinicianIdx] = useState<number | null>(null);

  // Fetch org members (consultants)
  const { data: members } = useMembers();
  const consultants = useMemo(
    () => (members ?? []).filter((m) => m.is_consultant && m.is_active),
    [members],
  );

  const analyzeImage = useAnalyzeScheduleImage({
    onSuccess: (data) => {
      setAppointments(
        data.appointments.map((apt) => ({
          ...apt,
          time: normalizeTime(apt.time),
          clinician_id: null,
        })),
      );
      setCurrentStep('review');
    },
    onError: () => {
      setCurrentStep('upload');
    },
  });

  const createAppointments = useCreateAppointments({
    onSuccess: () => {
      setCurrentStep('complete');
    },
  });

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleAnalyze = () => {
    if (!selectedFile) {
      toast.error('Please select a schedule image first');
      return;
    }

    const formData = new FormData();
    formData.append('image', selectedFile);

    setCurrentStep('analyzing');
    analyzeImage.mutate(formData);
  };

  const handleAppointmentChange = (
    index: number,
    field: keyof AppointmentWithClinician,
    value: string,
  ) => {
    const updated = [...appointments];
    updated[index] = { ...updated[index], [field]: value };
    setAppointments(updated);
  };

  const handleDateChange = (index: number, date: Date | undefined) => {
    if (!date) return;
    handleAppointmentChange(index, 'date', format(date, 'MM/dd/yyyy'));
  };

  const handleRemoveAppointment = (index: number) => {
    setAppointments(appointments.filter((_, i) => i !== index));
  };

  const handleClinicianChange = (index: number, clinicianUserId: string) => {
    const updated = [...appointments];
    updated[index] = { ...updated[index], clinician_id: clinicianUserId };
    setAppointments(updated);
  };

  const handleSubmit = () => {
    if (appointments.length === 0) {
      toast.error('No appointments to save. Add at least one.');
      return;
    }
    const missingClinician = appointments.some((a) => !a.clinician_id);
    if (missingClinician) {
      toast.error('Please select a clinician for every appointment');
      return;
    }

    createAppointments.mutate({
      appointments: appointments.map((a) => ({
        ...a,
        clinician_id: a.clinician_id!,
      })),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  };

  const handleReset = () => {
    setCurrentStep('upload');
    setSelectedFile(null);
    setImagePreview(null);
    setAppointments([]);
    setOpenClinicianIdx(null);
  };

  const currentStepIndex = STEPS.findIndex((s) => s.key === currentStep);

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Schedule Import</h1>
        <p className="text-muted-foreground mt-2">
          Snap a photo of your schedule and we'll create draft reports for each appointment
        </p>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center justify-center gap-2">
        {STEPS.map((step, index) => (
          <div key={step.key} className="flex items-center gap-2">
            <div className="flex flex-col items-center gap-1">
              <div
                className={cn(
                  'flex size-9 items-center justify-center rounded-full border-2 text-sm font-medium transition-colors',
                  currentStep === step.key && 'border-primary bg-primary text-primary-foreground',
                  index < currentStepIndex && 'border-green-500 bg-green-500 text-white',
                  index > currentStepIndex && 'border-muted bg-muted text-muted-foreground',
                )}
              >
                {index < currentStepIndex ? <CheckCircle2 className="size-4" /> : index + 1}
              </div>
              <span className="text-muted-foreground text-xs">{step.label}</span>
            </div>
            {index < STEPS.length - 1 && (
              <div
                className={cn(
                  'mb-5 h-0.5 w-12',
                  index < currentStepIndex ? 'bg-green-500' : 'bg-muted',
                )}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step 1: Upload */}
      {currentStep === 'upload' && (
        <Card>
          <CardHeader>
            <CardTitle>Upload your schedule</CardTitle>
            <CardDescription>
              Take a photo or select an image of your appointment schedule
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <label
              htmlFor="image-upload"
              className="border-border hover:border-primary hover:bg-accent/50 block cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors"
            >
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelect(file);
                }}
              />
              <div className="space-y-2">
                <ImagePlus className="text-muted-foreground mx-auto size-12" />
                <p className="text-muted-foreground text-sm">
                  Tap to take a photo or choose from gallery
                </p>
                <p className="text-muted-foreground/70 text-xs">
                  Supports PNG, JPG, JPEG up to 10 MB
                </p>
              </div>
            </label>

            {imagePreview && (
              <div className="space-y-2">
                <Label>Preview</Label>
                <img
                  src={imagePreview}
                  alt="Schedule preview"
                  className="max-h-96 rounded-lg border object-contain"
                />
                <p className="text-muted-foreground text-sm">{selectedFile?.name}</p>
              </div>
            )}
          </CardContent>
          <CardFooter className="justify-end">
            <Button onClick={handleAnalyze} disabled={!selectedFile}>
              <Upload className="size-4" />
              Read Schedule
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Step 2: Analyzing */}
      {currentStep === 'analyzing' && (
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="text-primary mx-auto mb-4 size-12 animate-spin" />
            <CardTitle className="mb-2">Reading your schedule...</CardTitle>
            <CardDescription>
              Extracting appointments from your image. This usually takes a few seconds.
            </CardDescription>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Review */}
      {currentStep === 'review' && (
        <Card>
          <CardHeader>
            <CardTitle>Review appointments ({appointments.length})</CardTitle>
            <CardDescription>
              Check the details below and correct anything that looks off before creating drafts.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {appointments.map((apt, index) => {
              const aptClinician = consultants.find((c) => c.user_id === apt.clinician_id);

              return (
                <Card key={index} className="bg-muted/30 py-4">
                  <CardHeader className="pb-0 pt-0">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Appointment {index + 1}</CardTitle>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveAppointment(index)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="size-4" />
                        Remove
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-3">
                    <div className="grid gap-4 md:grid-cols-2">
                      {/* Patient Name */}
                      <div className="space-y-2">
                        <Label htmlFor={`patient-${index}`}>Patient Name</Label>
                        <Input
                          id={`patient-${index}`}
                          value={apt.patient_name}
                          onChange={(e) =>
                            handleAppointmentChange(index, 'patient_name', e.target.value)
                          }
                        />
                      </div>

                      {/* DOB - Calendar Popover */}
                      <div className="space-y-2">
                        <Label>DOB</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="ghost"
                              className={cn(
                                'w-full justify-start text-left font-normal border border-input bg-transparent shadow-none hover:shadow-none hover:bg-transparent',
                                (!apt.dob || apt.dob === 'DOB not detected') &&
                                  'text-muted-foreground',
                              )}
                            >
                              <CalendarIcon className="text-muted-foreground size-4" />
                              {apt.dob && apt.dob !== 'DOB not detected' ? apt.dob : 'Pick DOB'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 shadow-none" align="start">
                            <Calendar
                              mode="single"
                              captionLayout="dropdown"
                              selected={parseDateString(apt.dob)}
                              onSelect={(date) => {
                                if (date)
                                  handleAppointmentChange(
                                    index,
                                    'dob',
                                    format(date, 'MM/dd/yyyy'),
                                  );
                              }}
                              defaultMonth={parseDateString(apt.dob) ?? new Date(1980, 0)}
                              disabled={{ after: new Date() }}
                              startMonth={new Date(1920, 0)}
                              endMonth={new Date()}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Time - Select */}
                      <div className="space-y-2">
                        <Label>Time</Label>
                        <Select
                          value={apt.time}
                          onValueChange={(val) => handleAppointmentChange(index, 'time', val)}
                        >
                          <SelectTrigger className="w-full shadow-none">
                            <Clock className="text-muted-foreground size-4" />
                            <SelectValue placeholder="Select time" />
                          </SelectTrigger>
                          <SelectContent className="shadow-none">
                            {TIME_OPTIONS.map((t) => (
                              <SelectItem key={t} value={t}>
                                {t}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Date - Calendar Popover */}
                      <div className="space-y-2">
                        <Label>Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="ghost"
                              className={cn(
                                'w-full justify-start text-left font-normal border border-input bg-transparent shadow-none hover:shadow-none hover:bg-transparent',
                                !apt.date && 'text-muted-foreground',
                              )}
                            >
                              <CalendarIcon className="size-4" />
                              {apt.date || 'Pick a date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 shadow-none" align="start">
                            <Calendar
                              mode="single"
                              selected={parseDateString(apt.date)}
                              onSelect={(date) => handleDateChange(index, date)}
                              defaultMonth={parseDateString(apt.date)}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Report Name */}
                      <div className="space-y-2">
                        <Label htmlFor={`report-${index}`}>Report Name</Label>
                        <Input
                          id={`report-${index}`}
                          value={apt.report_name}
                          onChange={(e) =>
                            handleAppointmentChange(index, 'report_name', e.target.value)
                          }
                        />
                      </div>

                      {/* Clinician */}
                      <div className="space-y-2">
                        <Label>Clinician</Label>
                        <Popover
                          open={openClinicianIdx === index}
                          onOpenChange={(open) => setOpenClinicianIdx(open ? index : null)}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              role="combobox"
                              variant="outline"
                              className={cn(
                                'w-full justify-between font-normal',
                                !apt.clinician_id && 'text-muted-foreground',
                              )}
                            >
                              {aptClinician ? (
                                <span className="truncate">{aptClinician.user_name}</span>
                              ) : (
                                'Assign clinician...'
                              )}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-[240px] p-0">
                            <Command>
                              <CommandInput placeholder="Search..." />
                              <CommandList>
                                <CommandEmpty>No clinicians found.</CommandEmpty>
                                <CommandGroup>
                                  {consultants.map((c) => (
                                    <CommandItem
                                      key={c.user_id}
                                      value={c.user_name ?? c.user_id}
                                      onSelect={() => {
                                        handleClinicianChange(index, c.user_id);
                                        setOpenClinicianIdx(null);
                                      }}
                                    >
                                      <Check
                                        className={cn(
                                          'mr-2 h-4 w-4',
                                          apt.clinician_id === c.user_id
                                            ? 'opacity-100'
                                            : 'opacity-0',
                                        )}
                                      />
                                      <span className="text-sm">{c.user_name}</span>
                                      <span className="ml-auto text-xs text-muted-foreground">
                                        {c.role_name}
                                      </span>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>

                    {/* Confidence badge */}
                    <div className="mt-3">
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                          apt.confidence >= 0.85
                            ? 'bg-green-100 text-green-700'
                            : apt.confidence >= 0.7
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700',
                        )}
                      >
                        {(apt.confidence * 100).toFixed(0)}% confidence
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </CardContent>
          <CardFooter className="justify-between">
            <Button variant="outline" onClick={handleReset}>
              Start Over
            </Button>
            <Button onClick={handleSubmit} disabled={createAppointments.isPending}>
              {createAppointments.isPending && <Loader2 className="size-4 animate-spin" />}
              {createAppointments.isPending ? 'Saving...' : 'Create Drafts'}
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Step 4: Complete */}
      {currentStep === 'complete' && (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle2 className="size-8 text-green-600" />
            </div>
            <CardTitle className="mb-2">All set!</CardTitle>
            <CardDescription className="mb-6">
              {appointments.length} draft{appointments.length !== 1 ? 's' : ''} created and ready
              for recording.
            </CardDescription>
            <Button onClick={handleReset}>Import Another Schedule</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
